import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Info } from "lucide-react";
import Reveal from "@/components/Reveal";
import { PRICING, BIZ } from "@/constants/content";

const DISCOUNT = 0.85;

function priceLabel(p, discounted) {
  if (p.price === null) return "Call";
  const v = discounted ? p.price * DISCOUNT : p.price;
  return `$${v.toFixed(2)}${p.plus ? "+" : ""}`;
}

export default function Pricing() {
  const [discounted, setDiscounted] = useState(false);

  return (
    <section id="pricing" className="relative py-20 sm:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow text-brandsky-deep">Pricing</p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
              Straightforward pricing.
              <br />
              No surprises.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="flex flex-col items-start gap-3 rounded-2xl border border-brandpink/25 bg-brandpink-soft p-5 lg:items-stretch">
              <label className="flex items-center justify-between gap-4" data-testid="pricing-discount-toggle-label">
                <span className="flex items-center gap-2 text-[14px] font-bold text-ink">
                  Preview your <span className="text-brandpink">15% first-order</span> discount
                </span>
                <Switch checked={discounted} onCheckedChange={setDiscounted} data-testid="pricing-discount-toggle" />
              </label>
              <p className="text-[12.5px] leading-relaxed text-ink/60">
                Flip the switch to see what first-time customers actually pay. It's applied automatically on your
                first order.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="overflow-hidden rounded-[24px] border border-ink/10 bg-white shadow-[0_14px_50px_rgba(22,50,79,0.09)]">
            <div className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-ink/10 bg-cloud/60 px-6 py-4 sm:px-8">
              <span className="eyebrow text-slate-500">Service</span>
              <span className="eyebrow text-right text-slate-500">Price</span>
            </div>
            <ul>
              {PRICING.map((p, i) => (
                <li
                  key={p.item}
                  data-testid={`pricing-item-${p.item.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={`grid grid-cols-[1fr_auto] items-center gap-4 px-6 py-5 sm:px-8 ${i % 2 ? "bg-paper/60" : ""} ${
                    p.price === null ? "bg-brandsky-soft/50" : ""
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-[15.5px] font-bold text-ink">{p.item}</h3>
                      {p.badge && (
                        <span className="rounded-full bg-brandpink px-2.5 py-0.5 text-[10.5px] font-extrabold uppercase tracking-wide text-white">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-[13px] text-slate-500">{p.detail}</p>
                  </div>
                  <div className="text-right">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={discounted ? "d" : "n"}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className={`block font-mono text-[17px] font-bold tabular-nums ${
                          p.price === null ? "text-brandsky-deep" : "text-ink"
                        }`}
                        data-testid={`pricing-price-${i}`}
                      >
                        {priceLabel(p, discounted)}
                      </motion.span>
                    </AnimatePresence>
                    {p.plus && <span className="text-[11px] text-slate-400">starting price</span>}
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 bg-white px-6 py-5 sm:px-8">
              <p className="flex items-center gap-2 text-[13.5px] font-semibold text-ink" data-testid="pricing-warranty">
                <ShieldCheck className="h-4 w-4 text-brandleaf-deep" />
                Every overhaul: 6-month warranty included · 12 months for $49
              </p>
              <p className="flex items-center gap-2 text-[13.5px] font-semibold text-ink" data-testid="pricing-warranty">
                <ShieldCheck className="h-4 w-4 text-brandleaf-deep" />
                Every overhaul: 6-month warranty included · 12 months for $49
              </p>
              <p className="flex items-center gap-1.5 text-[12px] font-semibold text-brandpink" data-testid="pricing-discount-note">
                <Info className="h-3.5 w-3.5" /> 15% first-order discount: write a note in your request to claim it
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-8 max-w-2xl text-center text-[14px] leading-relaxed text-slate-500" data-testid="pricing-beat-note">
            Something not listed here? <strong className="font-semibold text-ink">Please call</strong> — free estimates
            on everything. We can outsource all things dental and try to meet or beat competitor prices.
          </p>
        </Reveal>
      </div>
    </section>
  );
}